import HarvestFarmPicker from "./HarvestFarmPicker";
import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import FieldHarvestDataCapture from "./FieldHarvestDataCapture";
import "../../styles/HarvestQR.css";
import HarvestScanner from "./HarvestScanner";
import { newRequestId } from "../../utils/harvestScanQueue";

const root = "field-harvest/qr/";
const errorText = (e) =>
  typeof e.response?.data === "string"
    ? "The server could not complete the request."
    : JSON.stringify(e.response?.data || e.message || "Connection failed");
const fail = (e) =>
  Swal.fire({ title: "Action needed", text: errorText(e), icon: "error" });
export function AllocationRow({ code, codes, catalog, ghse, enabled, refresh }) {
  const [adder, setAdder] = useState(code.value_adder_id || ""),
    [bay, setBay] = useState(code.bay_id || ""),
    [busy, setBusy] = useState(false);
  const [reallocating, setReallocating] = useState(false);
  const currentAdder = catalog.adders.find((a) => a.id === code.value_adder_id);
  const currentBay = catalog.bays.find((b) => b.id === code.bay_id);
  const assigned = !!code.value_adder_id;
  const canEdit = !assigned || reallocating;
  const unavailable = new Set(codes.filter((other) => other.id !== code.id).map((other) => other.value_adder_id));
  const adders = catalog.adders.filter((a) => a.ghses.includes(Number(ghse)) && (!unavailable.has(a.id) || a.id === code.value_adder_id));
  useEffect(() => {
    if (adder && !adders.some((a) => a.id === Number(adder))) { setAdder(""); setBay(""); }
  }, [codes, adder, catalog, ghse]);
  const selected = adders.find((a) => a.id === Number(adder));
  const bays = catalog.bays.filter(
    (b) => b.ghse_id === Number(ghse) && selected?.bays.includes(b.id),
  );
  const unallocate = async () => {
    setBusy(true);
    try {
      const choice = await Swal.fire({
        title: `Unallocate QR #${code.number}?`,
        text: `Release ${currentAdder?.name || "this value adder"} so they can be assigned to another QR number in this greenhouse. Historical harvest remains unchanged. This number cannot record new scans until it is allocated again.`,
        icon: "warning", showCancelButton: true,
        confirmButtonText: "Yes, unallocate", cancelButtonText: "Keep allocation", confirmButtonColor: "#a92f6f",
      });
      if (!choice.isConfirmed) return;
      await api.post(root + "allocate/", { code: code.id, value_adder: null, bay: null, expected_value_adder: code.value_adder_id, expected_bay: code.bay_id });
      setReallocating(false);
      await refresh();
      await Swal.fire({ title: "Allocation released", text: "The value adder is available for another allocation. Historical harvest is unchanged.", icon: "success", confirmButtonColor: "#a92f6f" });
    } catch (e) { await fail(e); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const choice = await Swal.fire({
        title: assigned ? "Confirm reallocation" : "Confirm allocation",
        text: `${assigned ? `Current: ${currentAdder?.name || "Assigned member"} / ${currentBay?.name || "Assigned bay"}. New assignment applies to future scans only. Historical harvest stays unchanged. ` : ""}Assign QR #${code.number} to ${selected?.name} in ${catalog.ghses.find((g) => g.id === Number(ghse))?.name}, ${bays.find((b) => b.id === Number(bay))?.name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: assigned ? "Reallocate for future scans" : "Yes, save allocation",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#a92f6f",
      });
      if (!choice.isConfirmed) return;
      await api.post(root + "allocate/", {
        code: code.id,
        value_adder: Number(adder),
        bay: Number(bay),
        ...(assigned ? { expected_value_adder: code.value_adder_id, expected_bay: code.bay_id } : {}),
      });
      setReallocating(false);
      await refresh();
      await Swal.fire({ title: "Allocation saved", text: `QR #${code.number} is assigned to ${selected?.name}.`, icon: "success", confirmButtonColor: "#a92f6f" });
    } catch (e) {
      await fail(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className={reallocating ? "qr-reallocating" : ""}>
      <th>#{code.number}{reallocating && <small className="qr-reallocate-note">Future scans only</small>}</th>
      <td>
        {assigned && !reallocating ? <span className="qr-current-assignment">{currentAdder?.name || "Assigned member"}<small>{currentAdder?.payroll}</small></span> : <select
          aria-label={`Value adder for ${code.number}`}
          value={adder}
          disabled={!enabled || busy}
          onChange={(e) => {
            setAdder(e.target.value);
            setBay("");
          }}
        >
          <option value="">Unallocated</option>
          {adders.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.payroll})
            </option>
          ))}
        </select>}
      </td>
      <td>
        {assigned && !reallocating ? <span>{currentBay?.name || "Assigned bay"}</span> : <select
          aria-label={`Bay for ${code.number}`}
          value={bay}
          disabled={!enabled || busy}
          onChange={(e) => setBay(e.target.value)}
        >
          <option value="">Select bay</option>
          {bays.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>}
      </td>
      <td>
        {enabled && (
          !canEdit ? <><button type="button" disabled={busy} onClick={() => setReallocating(true)}>Reallocate</button><button type="button" disabled={busy} onClick={unallocate}>Unallocate</button></> : <>
            <button disabled={busy || !adder || !bay || (assigned && Number(adder) === code.value_adder_id && Number(bay) === code.bay_id)} onClick={save}>{assigned ? "Review reallocation" : "Save allocation"}</button>
            {reallocating && <button type="button" disabled={busy} onClick={() => { setAdder(code.value_adder_id); setBay(code.bay_id); setReallocating(false); }}>Cancel</button>}
          </>
        )}
      </td>
    </tr>
  );
}

function Management({ catalog, rights }) {
  const { user } = useAuth();
  const pendingKey = `smartharvest-print-pending-${user.id}`;
  const [pending, setPending] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(pendingKey));
    } catch {
      return null;
    }
  });
  const [ghse, setGhse] = useState(() =>
      pending?.ghse ? String(pending.ghse) : "",
    ),
    [data, setData] = useState({ codes: [], length: 0, batches: [] });
  const [length, setLength] = useState(30),
    [busy, setBusy] = useState(false),
    [page, setPage] = useState(0);
  const [range, setRange] = useState({ start: 1, end: 1, copies: 1 }),
    [lastBatch, setLastBatch] = useState(null);
  const load = async () => {
    if (!ghse) return;
    const { data: d } = await api.get(root + "codes/", { params: { ghse } });
    setData(d);
    setLength(d.length || 30);
  };
  useEffect(() => {
    let live = true;
    setPage(0);
    setData({ codes: [], length: 0, batches: [] });
    if (ghse) {
      setBusy(true);
      api
        .get(root + "codes/", { params: { ghse } })
        .then(({ data: d }) => {
          if (live) {
            setData(d);
            setLength(d.length || 30);
            setRange({ start: 1, end: 1, copies: 1 });
          }
        })
        .catch(fail)
        .finally(() => {
          if (live) setBusy(false);
        });
    }
    return () => {
      live = false;
    };
  }, [ghse]);
  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(root + "codes/", {
        ghse: Number(ghse),
        length: Number(length),
      });
      await load();
    } catch (e) {
      await fail(e);
    } finally {
      setBusy(false);
    }
  };
  const fetchPDF = async (batch) => {
    try {
      const { data: blob } = await api.get(root + "pdf/", {
        params: { batch },
        responseType: "blob",
        timeout: 180000,
      });
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = "SmartHarvest-labels.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          e.response.data = JSON.parse(await e.response.data.text());
        } catch {}
      }
      throw e;
    }
  };
  const print = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = pending || {
        request_id: newRequestId(),
        ghse: Number(ghse),
        start: Number(range.start),
        end: Number(range.end),
        copies: Number(range.copies),
      };
      localStorage.setItem(pendingKey, JSON.stringify(payload));
      setPending(payload);
      const { data: receipt } = await api.post(root + "pdf/", payload, {
        timeout: 60000,
      });
      setLastBatch(receipt.batch);
      localStorage.removeItem(pendingKey);
      setPending(null);
      await load();
      await fetchPDF(receipt.batch);
    } catch (e) {
      const status = e.response?.status;
      if (status && status < 500 && ![408, 409, 429].includes(status)) {
        localStorage.removeItem(pendingKey);
        setPending(null);
      }
      await fail(e);
    } finally {
      setBusy(false);
    }
  };
  const download = async (batch) => {
    setBusy(true);
    try {
      await fetchPDF(batch);
    } catch (e) {
      await fail(e);
    } finally {
      setBusy(false);
    }
  };
  const printAllocations = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get(root + "allocations-pdf/", { params: { ghse }, responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `SmartHarvest-allocations-GHSE-${ghse}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { e.response.data = JSON.parse(await e.response.data.text()); } catch {}
      }
      await fail(e);
    } finally { setBusy(false); }
  };
  const labelCount =
    (Number(range.end) - Number(range.start) + 1) * Number(range.copies);
  return (
    <section className="qr-card">
      <p className="qr-eyebrow">LABEL STUDIO</p>
      <h2>QR numbers & unique labels</h2>
      <p>
        Generate permanent numbers and allocate them to your existing field
        value adders. Choose any copy quantity when printing.
      </p>
<HarvestFarmPicker catalog={catalog} ghse={ghse} disabled={busy || !!pending} onChange={(value) => { setGhse(value); setLastBatch(null); }} />
      {ghse && (
        <>
          <p className="qr-summary">
            Permanent numbers:{" "}
            {data.length ? `1–${data.length}` : "Not generated yet"}
          </p>
          {rights.generate && (
            <form className="qr-grid" onSubmit={generate}>
              <label>
                Number range (1 to…)
                <input
                  type="number"
                  min={Math.max(1, data.length)}
                  max="10000"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  required
                  disabled={busy || !!pending}
                />
              </label>
              <button disabled={busy || !!pending}>
                Generate / extend numbers
              </button>
            </form>
          )}
          {rights.print && data.length > 0 && (
            <div className="qr-print-panel">
              <h3>Print unique labels</h3>
              <p>
                Select one number or a range. Every label gets its own hidden
                ID, including copies of the same number.
              </p>
              <form onSubmit={print}>
                <div className="qr-grid">
                  <label>
                    From QR number
                    <input
                      type="number"
                      min="1"
                      max={data.length}
                      value={range.start}
                      onChange={(e) =>
                        setRange({ ...range, start: e.target.value })
                      }
                      required
                      disabled={busy || !!pending}
                    />
                  </label>
                  <label>
                    To QR number
                    <input
                      type="number"
                      min={range.start}
                      max={data.length}
                      value={range.end}
                      onChange={(e) =>
                        setRange({ ...range, end: e.target.value })
                      }
                      required
                      disabled={busy || !!pending}
                    />
                  </label>
                  <label>
                    Copies per number
                    <input
                      type="number"
                      min="1"
                      max="12000"
                      value={range.copies}
                      onChange={(e) =>
                        setRange({ ...range, copies: e.target.value })
                      }
                      required
                      disabled={busy || !!pending}
                    />
                  </label>
                </div>
                <p>
                  {Number.isFinite(labelCount) && labelCount > 0
                    ? `${labelCount.toLocaleString()} unique labels`
                    : "Choose a valid range"}{" "}
                  · 30 labels per A4 page · fixed 30 mm QR size
                </p>
                {pending && (
                  <p className="qr-alert">
                    A print request is awaiting confirmation. Retry to recover
                    the same labels without issuing duplicates.
                  </p>
                )}
                <button
                  disabled={
                    busy || (!pending && (labelCount < 1 || labelCount > 12000))
                  }
                >
                  {busy
                    ? "Preparing labels…"
                    : pending
                      ? "Retry pending print"
                      : "Create labels & download PDF"}
                </button>
              </form>
              <p className="qr-help">
                The number, QR and small GHSE name appear on each label. Print at
                100%, one copy of each page. Downloading an existing batch again
                preserves its IDs; creating a new batch issues fresh IDs.
              </p>
              {lastBatch && (
                <button disabled={busy} onClick={() => download(lastBatch)}>
                  Download last batch again
                </button>
              )}
              {!!data.batches?.length && (
                <details>
                  <summary>Recent print batches</summary>
                  <ul>
                    {data.batches.map((b) => (
                      <li key={b.request_id}>
                        Numbers {b.start}–{b.end} · {b.copies_per_number} copies
                        each · {new Date(b.created_at).toLocaleString()}{" "}
                        <button
                          disabled={busy}
                          onClick={() => download(b.request_id)}
                        >
                          Download existing batch
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <h3>Allocate numbers to field value adders</h3>
          {rights.print && <button type="button" disabled={busy || !data.codes.length} onClick={printAllocations}>Print allocations PDF</button>}
          <p className="qr-help">Download all saved allocations for this GHSE, including unallocated numbers.</p>
          <p>
            All physical copies of a number use its allocation. Changes affect
            future scans; saved harvest remains unchanged.
          </p>
          <div className="qr-table">
            <table>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Value adder</th>
                  <th>Bay</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.codes.slice(page * 50, page * 50 + 50).map((code) => (
                  <AllocationRow
                    key={`${ghse}-${code.id}-${code.value_adder_id}-${code.bay_id}`}
                    code={code}
                    codes={data.codes}
                    catalog={catalog}
                    ghse={ghse}
                    enabled={rights.allocate}
                    refresh={load}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            disabled={(page + 1) * 50 >= data.codes.length}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </>
      )}
    </section>
  );
}

export function HarvestRights({ embedded = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null),
    [target, setTarget] = useState(""),
    [rights, setRights] = useState({}),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user)
      api
        .get(root + "rights/")
        .then((r) => setData(r.data))
        .catch(fail);
  }, [user]);
  if (!user) return <Navigate to="/login" replace />;
  if (!data) return <p>Loading rights…</p>;
  if (!data.admin) return <p>Only administrators can manage harvest rights.</p>;
  const choose = (value) => {
    setTarget(value);
    const [type, id] = value.split(":");
    setRights(
      type === "role"
        ? data.roles.find((r) => r.role === id)?.rights || {}
        : data.users.find((u) => u.id === Number(id))?.overrides || {},
    );
  };
  const save = async () => {
    setBusy(true);
    try {
      const [type, id] = target.split(":");
      const r = await api.put(root + "rights/", {
        [type]: type === "user" ? Number(id) : id,
        rights,
      });
      setData(r.data);
      await Swal.fire({
        title: "Rights saved",
        icon: "success",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      await fail(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="harvest-qr">
      {!embedded && <Link to="/manage?tab=harvest_rights">Back to Manage</Link>}
      <section className="qr-card">
        <p style={{ margin: 0, color: '#a92f6f', fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>Smart Harvest · Policy control</p>
        <h1>Access Control Studio</h1>
        <p>
          Set QR and field-harvest rights by role, then add individual overrides when needed. Administrators always retain full access.
        </p>
        <p style={{ fontSize: 13, color: '#6b5d65' }}>The role summary reflects Smart Harvest’s current route access. The editable policy below governs QR scanning, manual harvest capture, QR generation, allocation, and label printing.</p>
        <div style={{ overflowX: 'auto', margin: '22px 0', border: '1px solid #eadde4', borderRadius: 14 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720, textAlign: 'left' }}>
            <thead><tr style={{ background: '#f8f2f5' }}><th style={{ padding: 12 }}>Role</th><th style={{ padding: 12 }}>Core access</th><th style={{ padding: 12 }}>Harvest baseline</th></tr></thead>
            <tbody>{[
              ['admin', 'All management, capture, reports and settings', 'All rights · cannot be restricted'],
              ['qc', 'Packhouse capture and rejection workflows', 'Set QR/manual rights below'],
              ['team_leader', 'Production harvest capture; packhouse intake QC and rejections', 'Production defaults to scan + manual'],
              ['field_manager', 'Production manage + harvest; analytics', 'Production defaults to scan + manual'],
              ['packhouse_manager', 'Packhouse capture, intake QC, sessions, manage, rejections; analytics', 'Set QR/manual rights below'],
            ].map(([role, core, harvest]) => <tr key={role} style={{ borderTop: '1px solid #eee4e9' }}><th style={{ padding: 12, whiteSpace: 'nowrap' }}>{role.replaceAll('_', ' ')}</th><td style={{ padding: 12 }}>{core}</td><td style={{ padding: 12 }}>{harvest}</td></tr>)}</tbody>
          </table>
        </div>
        <h2 style={{ marginBottom: 6 }}>Role and user permission matrix</h2>
        <p style={{ marginTop: 0 }}>Choose a role or person to set scan, manual capture, QR generation, allocation, and label printing. User overrides take precedence over the role policy; “inherit” follows the role’s current setting.</p>
        <label>
          Edit policy for a role or person
          <select value={target} onChange={(e) => choose(e.target.value)}>
            <option value="">Select role or person</option>
            <optgroup label="Roles">
              {data.roles
                .filter((r) => r.role !== "admin")
                .map((r) => (
                  <option key={r.role} value={`role:${r.role}`}>
                    {r.name}
                  </option>
                ))}
            </optgroup>
            <optgroup label="People">
              {data.users
                .filter((u) => u.role !== "admin")
                .map((u) => (
                  <option key={u.id} value={`user:${u.id}`}>
                    {u.name} — {u.role}
                  </option>
                ))}
            </optgroup>
          </select>
        </label>
        {target && (
          <>
            {data.keys.map((k) => (
              <label key={k}>
                {
                  {
                    scan: "Scan QR codes",
                    manual: "Manual harvest capture",
                    generate: "Generate / increase QR sets",
                    allocate: "Allocate QR numbers",
                    print: "Issue unique labels / download PDFs",
                  }[k]
                }
                <select
                  value={
                    Object.hasOwn(rights, k) ? String(rights[k]) : "inherit"
                  }
                  onChange={(e) => {
                    const next = { ...rights };
                    if (e.target.value === "inherit") delete next[k];
                    else next[k] = e.target.value === "true";
                    setRights(next);
                  }}
                >
                  <option value="inherit">Inherit default / role</option>
                  <option value="true">Allow</option>
                  <option value="false">Deny</option>
                </select>
              </label>
            ))}
            <button disabled={busy} onClick={save}>
              Save rights
            </button>
          </>
        )}
      </section>
    </main>
  );
}

export default function HarvestQR() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null),
    [catalog, setCatalog] = useState(null),
    [tab, setTab] = useState("scan"),
    [locked, setLocked] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!user) return;
    let live = true;
    api
      .get(root + "rights/")
      .then(async (r) => {
        if (!live) return;
        setAccess(r.data);
        const d = await api.get(root + "catalog/");
        if (live) setCatalog(d.data);
      })
      .catch((e) => {
        if (live) setError(errorText(e));
      });
    return () => {
      live = false;
    };
  }, [user]);
  if (!user) return <Navigate to="/login" replace />;
  if (error)
    return (
      <main className="harvest-qr">
        <p role="alert">{error}</p>
      </main>
    );
  if (!access || !catalog) return <p>Loading harvest workspace…</p>;
  return (
    <main className="harvest-qr">
      <header className="qr-heading">
        <div>
          <p>SMARTHARVEST / FIELD OPERATIONS</p>
          <h1>Harvest capture</h1>
          <p>Scan. Assign. Keep harvesting.</p>
        </div>
      </header>
      <nav className="qr-tabs">
        {access.rights.scan && (
          <button
            className={tab === "scan" ? "active" : ""}
            onClick={() => setTab("scan")}
          >
            QR capture · primary
          </button>
        )}
        {(access.rights.generate ||
          access.rights.allocate ||
          access.rights.print) && (
          <button
            disabled={locked}
            className={tab === "manage" ? "active" : ""}
            onClick={() => setTab("manage")}
          >
            QR labels & allocation
          </button>
        )}
        {access.rights.manual && (
          <button
            disabled={locked}
            className={tab === "manual" ? "active" : ""}
            onClick={() => setTab("manual")}
          >
            Manual capture
          </button>
        )}
      </nav>
      {tab === "scan" &&
        (access.rights.scan ? (
          <HarvestScanner catalog={catalog} onLocked={setLocked} />
        ) : (
          <p>Select an available harvest tool above.</p>
        ))}
      {tab === "manage" && (
        <Management catalog={catalog} rights={access.rights} />
      )}{" "}
      {tab === "manual" && access.rights.manual && <FieldHarvestDataCapture />}
    </main>
  );
}
